import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";

import { parseMarkdown, type Block, type Inline } from "@/lib/help-markdown";

const LINK = "font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700";

function Inlines({ nodes, onNavigate }: { nodes: Inline[]; onNavigate?: () => void }): ReactNode {
  return nodes.map((node, i) => {
    switch (node.type) {
      case "text":
        return <Fragment key={i}>{node.text}</Fragment>;
      case "strong":
        return (
          <strong key={i} className="font-semibold text-fg">
            <Inlines nodes={node.children} onNavigate={onNavigate} />
          </strong>
        );
      case "em":
        return (
          <em key={i}>
            <Inlines nodes={node.children} onNavigate={onNavigate} />
          </em>
        );
      case "code":
        return (
          <code key={i} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">
            {node.text}
          </code>
        );
      case "link": {
        const children = <Inlines nodes={node.children} onNavigate={onNavigate} />;
        // `parseMarkdown` only keeps http(s), mailto and app-relative hrefs.
        if (node.href.startsWith("/")) {
          return (
            <Link key={i} to={node.href} onClick={onNavigate} className={LINK}>
              {children}
            </Link>
          );
        }
        return (
          <a
            key={i}
            href={node.href}
            rel="noopener noreferrer"
            target={node.href.startsWith("mailto:") ? undefined : "_blank"}
            className={LINK}
          >
            {children}
          </a>
        );
      }
    }
  });
}

function plainText(nodes: Inline[]): string {
  return nodes.map((n) => (n.type === "text" || n.type === "code" ? n.text : plainText(n.children))).join("");
}

/**
 * A help article body. It draws the token tree from `parseMarkdown` as React elements, so article
 * text is never HTML. Headings sit below the panel's own title (an `h2`): levels 1-2 are `h3`, 3 is `h4`.
 */
export function HelpMarkdown({
  body,
  title,
  onNavigate,
}: {
  body: string;
  /** The article title: a leading heading that repeats it is left out, as the panel already shows it. */
  title?: string;
  /** Called when an in-app link is followed, so the panel can close. */
  onNavigate?: () => void;
}) {
  let blocks: Block[] = parseMarkdown(body);
  const first = blocks[0];
  if (
    title &&
    first?.type === "heading" &&
    first.level === 1 &&
    plainText(first.children).trim().toLowerCase() === title.trim().toLowerCase()
  ) {
    blocks = blocks.slice(1);
  }

  return (
    <div className="space-y-3 text-[13.5px] leading-[1.6] text-fg-muted">
      {blocks.map((block, i) => {
        switch (block.type) {
          case "heading": {
            const Tag = block.level === 3 ? "h4" : "h3";
            return (
              <Tag key={i} className="pt-2 text-sm font-semibold text-fg">
                <Inlines nodes={block.children} onNavigate={onNavigate} />
              </Tag>
            );
          }
          case "paragraph":
            return (
              <p key={i}>
                <Inlines nodes={block.children} onNavigate={onNavigate} />
              </p>
            );
          case "list": {
            const Tag = block.ordered ? "ol" : "ul";
            return (
              <Tag key={i} className={`${block.ordered ? "list-decimal" : "list-disc"} space-y-1 pl-5`}>
                {block.items.map((item, j) => (
                  <li key={j}>
                    <Inlines nodes={item} onNavigate={onNavigate} />
                  </li>
                ))}
              </Tag>
            );
          }
          case "code_block":
            return (
              <pre key={i} className="overflow-x-auto rounded-lg bg-muted p-3 font-mono text-xs text-fg">
                <code>{block.text}</code>
              </pre>
            );
        }
      })}
    </div>
  );
}
